/**
 * Layout del grafo de modelos: funcion PURA, sin React ni React Flow dentro.
 *
 * Vive aparte del componente por dos razones. La primera es que asi se puede
 * testear la geometria (que dos cajas no se solapan, que un agente sin
 * subagentes no produce aristas, que ningun hijo se sale de su grupo) sin
 * montar el canvas — que en jsdom mide 0 y no pinta nada, con lo que un test
 * sobre el componente NO puede afirmar nada de las posiciones. La segunda es
 * que la regla eslint `react-refresh/only-export-components` exige que un
 * fichero de componente no exporte otra cosa, el mismo criterio que
 * `pestanasEstado.ts`.
 *
 * **No hay motor de layout** (dagre y compania) a proposito: la forma de este
 * grafo se conoce de antemano —agentes en columna, sus subagentes a la derecha,
 * tareas en su propio grupo— y se calcula con aritmetica. Un motor generico
 * es para grafos cuya forma no sabes, y aqui solo aportaria una dependencia
 * mas al `package.json`, que en este repo lleva pins delicados.
 *
 * **Dos grupos, y solo dos: Agentes y Tareas.** Lo eligio el usuario tras
 * discutir criterios (por tipo de grafo, por conversacional/autonomo, por
 * numero de subagentes...): son PERTENENCIA visual, no jerarquia. Los
 * subagentes siguen colgando de su agente con una arista DENTRO del grupo de
 * Agentes; las tareas (operaciones auxiliares) no cuelgan de nadie y van en el
 * suyo. Se modelan como **sub flows** de React Flow: un nodo contenedor de tipo
 * `grupo` y sus hijos con `parentId`, cuya `position` pasa a ser RELATIVA al
 * grupo. Por eso hay que sumar la del padre para comparar dos cajas de grupos
 * distintos (los tests lo hacen).
 */
import { tipoConocido, type Asignacion } from "@/lib/api/modelosApi";
import { contarArrastres } from "./filtroAsignaciones";

/** Datos que el componente de nodo recibe. La asignacion entera, sin recortar. */
export interface DatosNodo extends Record<string, unknown> {
  asignacion: Asignacion;
}

export type Grupo = "agentes" | "tareas";

/** Datos del nodo contenedor: que grupo es y cuantos targets contiene. */
export interface DatosGrupo extends Record<string, unknown> {
  grupo: Grupo;
  num: number;
}

/** Nodo de target en la forma que espera React Flow (sin importar sus tipos aqui). */
export interface NodoGrafo {
  id: string;
  type: "targetModelo";
  /** RELATIVA a su grupo (`parentId`), como manda React Flow para los sub flows. */
  position: { x: number; y: number };
  data: DatosNodo;
  /** Alto/ancho declarados: React Flow los necesita para `fitView` sin medir. */
  width: number;
  height: number;
  /** Grupo al que pertenece. Siempre hay uno: no existen targets sueltos. */
  parentId: string;
  draggable: false;
  connectable: false;
}

/**
 * Nodo contenedor de un grupo. NO es editable, ni seleccionable, ni
 * arrastrable, ni enfocable, y no lleva selector: es un marco con titulo.
 *
 * Que React Flow le ponga `pointer-events: none` por no ser seleccionable ni
 * arrastrable es aqui lo DESEADO —el puntero lo atraviesa y el pan del fondo
 * sigue funcionando sobre el marco—, al contrario que en los hijos, donde esa
 * misma regla mataria el `<select>` (ver `elementsSelectable` en el componente).
 */
export interface NodoGrupo {
  id: string;
  type: "grupo";
  position: { x: number; y: number };
  data: DatosGrupo;
  width: number;
  height: number;
  selectable: false;
  draggable: false;
  connectable: false;
  focusable: false;
}

export interface AristaGrafo {
  id: string;
  source: string;
  target: string;
}

export interface Layout {
  /** Los grupos PRIMERO y despues sus hijos: el orden que documenta React Flow. */
  nodes: (NodoGrupo | NodoGrafo)[];
  edges: AristaGrafo[];
}

/** Ids fijos de los dos nodos contenedores. Con prefijo: ningun target se llama asi. */
export const ID_GRUPO: Record<Grupo, string> = {
  agentes: "grupo:agentes",
  tareas: "grupo:tareas",
};

// Geometria. Todo en px del sistema de coordenadas de React Flow (el zoom es
// otra cosa). Se declaran como constantes exportadas porque los tests afirman
// solapamiento con ellas y no con numeros magicos repetidos.
export const ANCHO_NODO = 290;

/**
 * Alto de una caja: **depende de lo que ESA caja pinta**, no es universal.
 *
 * Los bloques del nodo llevan `shrink-0`: sin el, un alto declarado CORTO no
 * desbordaria sino que el flex encogeria el id del modelo hasta hacerlo
 * desaparecer (medido: a 0 px con una fila de insignias de mas). Desbordar se
 * ve; encoger no.
 *
 * Hubo un alto fijo (162) que reservaba para todas el peor caso —dos filas de
 * insignias, que solo `factura_compra` necesita— y las demas pagaban ese
 * hueco en blanco entre las insignias y el desplegable (unos 45 px por caja,
 * medido en Chrome). Ahora cada nodo declara el alto de su contenido y el
 * layout ACUMULA con ese numero, asi que la caja de una tarea es mas baja que
 * la de un agente, y la de un agente sin subagentes mas baja que la de uno
 * que avisa de a cuantos arrastra.
 *
 * Los sumandos estan MEDIDOS en Chrome con el payload real (28 cajas, es y en)
 * y los estilos del nodo (`GrafoModelos.tsx`): borde 1 px, `py-2`, `gap-1.5`
 * entre bloques, nombre `text-sm`, id del modelo `text-[11px]`, insignias
 * `text-[10px] leading-tight py-0.5`, aviso `text-[10px]`, `<select>` `text-xs
 * py-1`. **Un alto declarado que no case con lo que el DOM renderiza produce
 * solapes que el layout no ve**: si se toca el CSS del nodo, se vuelve a medir.
 */
/**
 * Borde (2) + `py-2` (16) + fila del nombre (20) + id del modelo (16,5) +
 * fila del desplegable (29: el `<select>` mide mas que su linea de texto) +
 * dos `gap-1.5` (12) = 95,5.
 */
export const ALTO_BASE = 96;
/** Una fila de insignias (16,5) mas el `gap-1.5` que la separa (6) = 22,5. */
export const ALTO_FILA_INSIGNIAS = 23;
/** Cada fila de insignias ADICIONAL: la fila (16,5) mas el `gap-1` del `flex-wrap` (4). */
export const ALTO_FILA_INSIGNIAS_EXTRA = 21;
/** La linea "Cambiarlo mueve tambien a N subagentes" (15) mas su gap (6). */
export const ALTO_AVISO_ARRASTRE = 21;
/**
 * Cuantas insignias se dan por seguras en UNA fila a `ANCHO_NODO` (264 px
 * utiles). Dos caben siempre. Tres NO siempre: en ingles "Custom graph" +
 * "Autonomous" + "Human approval" miden 84,8 + 76,3 + 96,7 + 8 de gaps =
 * 265,8 px y envuelven (medido en Chrome con Poppins; en castellano el peor
 * trio, 75,1 + 64,7 + 116,2 + 8 = 264,0, cabe por CERO px). Cuatro envuelven
 * en los dos idiomas. Se estima con `ceil(n / 2)`: para 3 insignias reserva
 * una fila que en castellano quedaria vacia (21 px de sobrante), y es a
 * proposito. El fallo de estimar de MENOS es un solape silencioso con la caja
 * de debajo; el de estimar de MAS, un hueco que se ve. Hoy ningun agente lleva
 * tres. Si se añade una insignia o se alargan las etiquetas, volver a medir.
 */
export const INSIGNIAS_POR_FILA = 2;

/**
 * Cuantas filas de insignias pinta la caja de `a`. Reproduce las reglas de
 * `InsigniasAgente`: sin `caracteristicas` (una tarea) no hay fila; el tipo
 * solo si el front lo conoce; el eje chat/autonomo NO se pinta en un
 * subagente; aprobacion humana y adjunto solo cuando son verdad.
 */
export function filasInsignias(a: Asignacion): number {
  const c = a.caracteristicas;
  if (!c) return 0;
  let n = 0;
  if (tipoConocido(c.tipo)) n += 1;
  if (a.target_type !== "subagent") n += 1;
  if (c.hitl) n += 1;
  if (c.requires_document) n += 1;
  return Math.ceil(n / INSIGNIAS_POR_FILA);
}

/**
 * Alto de la caja de `a`. `arrastra` es a cuantos subagentes moveria un
 * cambio en ella (`contarArrastres`): con mas de 0 la caja lleva la linea de
 * aviso. Es la MISMA funcion que usa el nodo para su `height`, asi que layout
 * y DOM no pueden contar cosas distintas.
 */
export function altoNodo(a: Asignacion, arrastra: number): number {
  const filas = filasInsignias(a);
  let alto = ALTO_BASE;
  if (filas > 0) alto += ALTO_FILA_INSIGNIAS + (filas - 1) * ALTO_FILA_INSIGNIAS_EXTRA;
  if (arrastra > 0) alto += ALTO_AVISO_ARRASTRE;
  return alto;
}

/** Separacion vertical entre dos cajas consecutivas de la misma columna. */
export const HUECO_Y = 16;
/** Separacion horizontal entre la columna de agentes y la de subagentes. */
export const HUECO_X = 120;
/** Cuantas cajas caben en una fila de rejilla (agentes sueltos y tareas). */
export const COLUMNAS_AUXILIARES = 3;
/** Margen interior del grupo, entre el marco y sus cajas. */
export const MARGEN_GRUPO = 28;
/** Alto reservado arriba de cada grupo para su titulo. */
export const CABECERA_GRUPO = 48;
/** Hueco VERTICAL entre los dos grupos apilados. */
export const HUECO_GRUPOS = 96;

const PASO_X = ANCHO_NODO + HUECO_X;

/**
 * Traduce la lista plana del backend al grafo que se dibuja.
 *
 * Reglas, todas con test:
 * - Dos grupos, **Agentes** (agentes y subagentes) y **Tareas** (operaciones
 *   auxiliares), APILADOS en vertical. Cada grupo se DIMENSIONA por su
 *   contenido: si mañana hay un agente mas, el marco crece; no hay medidas
 *   fijas de grupo.
 * - Un grupo sin contenido **no se pinta** (ni marco ni titulo). Bajo el
 *   filtro, un marco vacio con titulo parece un fallo de carga —mismo criterio
 *   que la tabla, que tampoco se pinta sin filas—, y el "sin coincidencias" ya
 *   lo dice el filtro una vez, arriba: repetirlo dentro del marco seria un
 *   segundo sitio diciendo lo mismo. El grupo que queda empieza en el origen.
 * - Los agentes CON subagentes van en una columna a la izquierda del grupo,
 *   **en el orden en que los devuelve el backend**: es el orden del registro y
 *   el admin ya lo conoce de la tabla. Reordenar aqui seria una segunda verdad
 *   sobre el orden.
 * - Los subagentes de cada agente van a su derecha, apilados, con una arista
 *   desde el padre.
 * - Los agentes SIN subagentes van despues, en rejilla compacta: no necesitan
 *   una fila entera y apilarlos es lo que hace que el grafo quepa legible (ver
 *   el comentario del cuerpo, con la medida real).
 * - La altura que ocupa un agente depende de CUANTOS subagentes tiene y de lo
 *   ALTA que es cada caja (`altoNodo`), y la posicion vertical del siguiente
 *   se calcula acumulando. Con un paso fijo, `especificaciones` (3 subagentes)
 *   se comeria las cajas de debajo; con un alto fijo, todas las cajas pagarian
 *   en blanco el alto de la mas cargada.
 * - Las tareas **no cuelgan de ningun agente**: corren para todo el sistema.
 *   Van en su grupo, en rejilla y SIN aristas. Colgarlas de un agente
 *   afirmaria una relacion que no existe.
 * - Un subagente cuyo `parent` no casa con ningun agente de la lista no se
 *   pierde: se trata como un target suelto DENTRO del grupo de Agentes (es un
 *   subagente, no una tarea), sin arista. Descartarlo dejaria un target
 *   invisible e inasignable sin que nada avisara.
 *
 * Los dos grupos van APILADOS en vertical (Agentes arriba, Tareas debajo), por
 * decision del usuario: leer la pantalla de arriba abajo pesa mas que encajar
 * mas grafo de una vez. Estuvieron lado a lado hasta el 2026-09-21, y el
 * cambio tiene un COSTE medido que conviene no olvidar antes de "arreglarlo":
 * con el zoom de encuadre en su minimo de 0,45 (por debajo no se lee), en el
 * canvas de pagina entra ~la mitad del grafo a lo alto en vez del 80 %, y hay
 * que desplazarse o abrir a pantalla completa para ver las Tareas. A cambio,
 * el ancho deja de ser el problema: el grafo pasa a ser estrecho y alto, que
 * es lo que mejor sienta a una ventana estrecha y al scroll natural.
 */
export function construirLayout(asignaciones: Asignacion[]): Layout {
  const agentes = asignaciones.filter((a) => a.target_type === "agent");
  const subagentes = asignaciones.filter((a) => a.target_type === "subagent");
  const operaciones = asignaciones.filter((a) => a.target_type === "operation");

  const idsAgentes = new Set(agentes.map((a) => a.target_id));
  const porPadre = new Map<string, Asignacion[]>();
  const huerfanos: Asignacion[] = [];
  for (const s of subagentes) {
    const padre = s.parent;
    if (padre && idsAgentes.has(padre)) {
      const grupo = porPadre.get(padre);
      if (grupo) grupo.push(s);
      else porPadre.set(padre, [s]);
    } else {
      huerfanos.push(s);
    }
  }

  const edges: AristaGrafo[] = [];
  // El aviso "mueve a N subagentes" ocupa una linea: la caja de un agente que
  // lo lleva es mas alta, y el layout tiene que saberlo con el MISMO criterio
  // con el que el nodo decide pintarlo.
  const arrastres = contarArrastres(asignaciones);
  const caja = (a: Asignacion, x: number, y: number, parentId: string) =>
    nodo(a, x, y, parentId, altoNodo(a, arrastres.get(a.target_id) ?? 0));

  // ---- Grupo Agentes -------------------------------------------------------
  const hijosAgentes: NodoGrafo[] = [];
  let y = 0;

  // Primero los agentes CON subagentes, cada uno con su bloque de hijos a la
  // derecha; despues, los que no tienen, en rejilla compacta.
  //
  // La separacion no es estetica, es LEGIBILIDAD MEDIDA: con los 14 targets con
  // padre y los 9 agentes en una sola columna, el grafo real mide ~1.700 px de
  // alto por 640 de ancho, y `fitView` lo encaja a **zoom 0,21** — se ve entero
  // y no se lee NADA (comprobado en Chrome). Los agentes sin subagentes no
  // necesitan una fila entera para si solos: apilarlos en rejilla usa el ancho
  // que el bloque de hijos deja libre y baja la altura a un tercio.
  const conHijos = agentes.filter((a) => (porPadre.get(a.target_id) ?? []).length > 0);
  const sinHijos = agentes.filter((a) => (porPadre.get(a.target_id) ?? []).length === 0);

  for (const agente of conHijos) {
    const hijos = porPadre.get(agente.target_id)!;
    // El agente se centra verticalmente respecto a su bloque de hijos: con 3
    // hijos, la arista del de en medio sale recta y las otras dos abren en
    // abanico, que es lo que hace legible de un vistazo quien cuelga de quien.
    // El bloque mide lo que lo mas alto de los dos lados: con UN hijo la caja
    // del padre (que lleva la linea de aviso) es mas alta que la del hijo, y
    // centrar el padre sobre el hijo lo sacaria por arriba.
    const cajasHijos = hijos.map((h) => caja(h, PASO_X, 0, ID_GRUPO.agentes));
    const altoHijos = apilado(cajasHijos.map((c) => c.height));
    const padre = caja(agente, 0, 0, ID_GRUPO.agentes);
    const alturaBloque = Math.max(altoHijos, padre.height);

    padre.position.y = y + Math.round((alturaBloque - padre.height) / 2);
    hijosAgentes.push(padre);

    let yHijo = y + Math.round((alturaBloque - altoHijos) / 2);
    for (const hijo of cajasHijos) {
      hijo.position.y = yHijo;
      yHijo += hijo.height + HUECO_Y;
      hijosAgentes.push(hijo);
      edges.push({
        id: `e:${agente.target_id}->${hijo.id}`,
        source: agente.target_id,
        target: hijo.id,
      });
    }

    y += alturaBloque + HUECO_Y;
  }

  // Los agentes sueltos y, tras ellos, los subagentes huerfanos (la anomalia,
  // en su grupo y no entre las tareas), en la misma rejilla de anchura que las
  // tareas para que las columnas de los dos grupos queden alineadas.
  hijosAgentes.push(...rejilla([...sinHijos, ...huerfanos], y, ID_GRUPO.agentes, caja));

  // ---- Grupo Tareas --------------------------------------------------------
  const hijosTareas = rejilla(operaciones, 0, ID_GRUPO.tareas, caja);

  // ---- Los marcos, dimensionados por su contenido y APILADOS en vertical ----
  const nodes: Layout["nodes"] = [];
  let yMarco = 0;
  for (const [grupo, hijos] of [
    ["agentes", hijosAgentes],
    ["tareas", hijosTareas],
  ] as const) {
    if (hijos.length === 0) continue; // un grupo vacio no se pinta
    const marco = enmarcar(grupo, hijos, yMarco);
    nodes.push(marco);
    yMarco += marco.height + HUECO_GRUPOS;
  }
  // Los hijos DESPUES de todos los grupos, como documenta React Flow.
  nodes.push(...hijosAgentes, ...hijosTareas);

  return { nodes, edges };
}

export interface Rectangulo {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Rectangulo que abarca el grafo entero, en coordenadas del grafo, o `null` si
 * no hay nada que dibujar.
 *
 * Sale del LAYOUT y no del store de React Flow a proposito: los grupos estan
 * en coordenadas absolutas y encierran a todos sus hijos, asi que la union de
 * sus marcos es el grafo. Pedirselo al canvas (`getNodes()` + `getNodesBounds`)
 * fallaba en el momento del encuadre inicial —medido en Chrome: el store aun
 * no tenia los nodos cuando el canvas ya media, y el encuadre salia para un
 * rectangulo vacio (zoom 1 en el centro del canvas)—, y ademas los hijos de un
 * grupo llevan posicion RELATIVA, que esa funcion no resuelve sin el lookup
 * interno.
 */
export function limitesLayout(layout: Layout): Rectangulo | null {
  const grupos = layout.nodes.filter((n): n is NodoGrupo => n.type === "grupo");
  if (grupos.length === 0) return null;
  const x0 = Math.min(...grupos.map((g) => g.position.x));
  const y0 = Math.min(...grupos.map((g) => g.position.y));
  const x1 = Math.max(...grupos.map((g) => g.position.x + g.width));
  const y1 = Math.max(...grupos.map((g) => g.position.y + g.height));
  return { x: x0, y: y0, width: x1 - x0, height: y1 - y0 };
}

/**
 * Construye el nodo contenedor de un grupo a partir de sus hijos y los
 * DESPLAZA dentro del marco (margen + cabecera). El tamaño sale de la caja mas
 * a la derecha y de la mas baja: añadir un agente lo hace crecer solo.
 */
function enmarcar(grupo: Grupo, hijos: NodoGrafo[], y: number): NodoGrupo {
  let maxX = 0;
  let maxY = 0;
  for (const h of hijos) {
    maxX = Math.max(maxX, h.position.x + ANCHO_NODO);
    maxY = Math.max(maxY, h.position.y + h.height);
    h.position = { x: h.position.x + MARGEN_GRUPO, y: h.position.y + CABECERA_GRUPO };
  }
  return {
    id: ID_GRUPO[grupo],
    type: "grupo",
    position: { x: 0, y },
    data: { grupo, num: hijos.length },
    width: maxX + MARGEN_GRUPO * 2,
    height: maxY + CABECERA_GRUPO + MARGEN_GRUPO,
    selectable: false,
    draggable: false,
    connectable: false,
    focusable: false,
  };
}

/** Alto de N cajas apiladas con `HUECO_Y` entre ellas. */
function apilado(altos: number[]): number {
  return altos.reduce((s, h) => s + h, 0) + Math.max(0, altos.length - 1) * HUECO_Y;
}

/**
 * Rejilla de `COLUMNAS_AUXILIARES` columnas a partir de `y0`. Cada fila mide
 * lo que su caja MAS ALTA: las cajas de una fila arrancan alineadas arriba y
 * la fila siguiente empieza donde acaba la mas baja de la anterior, asi que
 * una tarea (sin insignias) al lado de un agente no deja hueco de mas ni se
 * come a la de debajo.
 */
function rejilla(
  items: Asignacion[],
  y0: number,
  parentId: string,
  caja: (a: Asignacion, x: number, y: number, parentId: string) => NodoGrafo,
): NodoGrafo[] {
  const out: NodoGrafo[] = [];
  let y = y0;
  for (let i = 0; i < items.length; i += COLUMNAS_AUXILIARES) {
    const fila = items
      .slice(i, i + COLUMNAS_AUXILIARES)
      .map((a, col) => caja(a, col * PASO_X, y, parentId));
    out.push(...fila);
    y += Math.max(...fila.map((n) => n.height)) + HUECO_Y;
  }
  return out;
}

function nodo(
  asignacion: Asignacion,
  x: number,
  y: number,
  parentId: string,
  height: number,
): NodoGrafo {
  return {
    id: asignacion.target_id,
    type: "targetModelo",
    position: { x, y },
    data: { asignacion },
    width: ANCHO_NODO,
    height,
    parentId,
    // Topologia de solo lectura: ni se arrastran los nodos ni se crean
    // conexiones. Se declara tambien por nodo, y no solo en el `<ReactFlow>`,
    // para que un futuro `nodesDraggable` olvidado no convierta el panel en un
    // editor de diagramas donde mover una caja no significa nada.
    // OJO: aqui NO va `selectable: false`. React Flow apaga los eventos de
    // puntero de un nodo que no es seleccionable ni arrastrable, y entonces el
    // `<select>` de dentro no abre (medido en Chrome, ver el componente).
    draggable: false,
    connectable: false,
  };
}
